import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import type {
  ErrorEventFrame,
  ErrorEventHandler,
  NotificationEventFrame,
  NotificationEventHandler,
  PushFrameMeta,
} from '@localagent/sdk';
import { bridgeEventStreamAtom, daemonPushFrameStreamAtom } from '../stores/sdk/atoms';

/** Either transport's notification/error demux — the two are interchangeable here. */
interface PushFrameSource {
  onNotificationEvent(handler: NotificationEventHandler): () => void;
  onErrorEvent(handler: ErrorEventHandler): () => void;
}

/**
 * Subscribe to notification/error PUSH frames from the ONE connection this
 * platform published — the cloud bridge on web, the daemon's `/events` relay on
 * desktop (events ADR-012). This is the shared substrate under
 * both `useNotificationStream` (feeds the stores) and the
 * `NotificationToastDispatcher` (raises toasts): each registers its own handler
 * set on the SAME stream, never a second socket. Both live (singular) and
 * replayed (plural) frames arrive; the SDK demux dedups by `event_id`.
 *
 * Handlers should be referentially STABLE (wrap in `useCallback`) so the
 * subscription is not torn down and rebuilt on every render.
 */
export function usePushFrameSubscription(handlers: {
  onNotification: (frame: NotificationEventFrame, meta: PushFrameMeta) => void;
  onError: (frame: ErrorEventFrame, meta: PushFrameMeta) => void;
}): void {
  const { onNotification, onError } = handlers;
  const cloudStream = useAtomValue(bridgeEventStreamAtom);
  const daemonStream = useAtomValue(daemonPushFrameStreamAtom);
  const stream: PushFrameSource | null = cloudStream ?? daemonStream;

  useEffect(() => {
    if (!stream) return;
    const offNotification = stream.onNotificationEvent(onNotification);
    const offError = stream.onErrorEvent(onError);
    return () => {
      offNotification();
      offError();
    };
  }, [stream, onNotification, onError]);
}
